import * as _4 from 'lodash4';
import { ConditionalStrategy, Strategy } from 'utility-ts-strategy';
import {
  FlaggedPlanFeedbackCourse,
  FlaggedPlanFeedbackCourseGroup,
  PlanFeedbackServant
} from '../servants/plan_feedback_servant';
import { IPlanFeedbackCourse } from './plan_feedback_course_builder';
import { IPlanFeedbackCourseGroup } from './plan_feedback_course_group_builder';
import { PlanFeedbackProgramSlot } from './plan_feedback_program_slot_builder';
import {
  IPlanFeedbackSuggestion,
  PlanFeedbackGenericSlotType,
  planFeedbackTransactionType
} from './plan_feedback_transaction_type';

const PlanFeedbackGenericSlotBuilderStrategy = new ConditionalStrategy<
  FlaggedPlanFeedbackCourse | FlaggedPlanFeedbackCourseGroup | PlanFeedbackProgramSlot,
  'course' | 'course_group' | 'program_slot'
>({
  course: (suggestion): suggestion is FlaggedPlanFeedbackCourse => _4.has(suggestion, 'course'),
  course_group: (suggestion): suggestion is FlaggedPlanFeedbackCourseGroup => _4.has(suggestion, 'course_group'),
  program_slot: (suggestion): suggestion is PlanFeedbackProgramSlot =>
    !_4.has(suggestion, 'course_group') && !_4.has(suggestion, 'course') && !_4.has(suggestion, 'from_term')
});

const PlanFeedbackGenericSlotCssClassStrategy = new Strategy({
  ADD: 'clr-green_40-txt text-bold',
  REMOVE: 'text-strike clr-red_40-txt',
  NONE: 'text-ellipsis clr-green_40-txt',
  MET: 'text-ellipsis clr-green_40-txt',
  UNMET: 'text-ellipsis clr-green_40-txt'
});

const PlanFeedbackGenericSlotCourseScreenReaderTextStrategy = new Strategy({
  ADD: (feedbackCourse: IPlanFeedbackCourse) =>
    `Added Course ${feedbackCourse.course.cd} ${feedbackCourse.course.title}`,
  REMOVE: (feedbackCourse: IPlanFeedbackCourse) =>
    `Removed Course ${feedbackCourse.course.cd} ${feedbackCourse.course.title}`,
  NONE: (feedbackCourse: IPlanFeedbackCourse) => `${feedbackCourse.course.cd} ${feedbackCourse.course.title}`
});

const PlanFeedbackGenericSlotProgramSlotScreenReaderTextStrategy = new Strategy({
  NONE: (planFeedbackPgmSlot: PlanFeedbackProgramSlot) => `Reason changed to "${planFeedbackPgmSlot.comment}"`,
  MET: (planFeedbackPgmSlot: PlanFeedbackProgramSlot) => `Marked ${planFeedbackPgmSlot.program_slot_obj.desc} as met`,
  UNMET: (planFeedbackPgmSlot: PlanFeedbackProgramSlot) =>
    `Marked ${planFeedbackPgmSlot.program_slot_obj.desc} as unmet`
});

const PlanFeedbackGenericSlotCourseGroupScreenReaderTextStrategy = new Strategy({
  ADD: (feedbackCourseGroup: IPlanFeedbackCourseGroup) =>
    `Added ${feedbackCourseGroup.program_slot_obj.desc} placeholder`,
  REMOVE: (feedbackCourseGroup: IPlanFeedbackCourseGroup) =>
    `Removed ${feedbackCourseGroup.program_slot_obj.desc} placeholder`,
  NONE: (feedbackCourseGroup: IPlanFeedbackCourseGroup) => `${feedbackCourseGroup.program_slot_obj.desc} placeholder`
});

const PlanFeedbackGenericSlotBuilderFns = new Strategy({
  course: (suggestion: FlaggedPlanFeedbackCourse) => ({
    id: suggestion.id,
    slotTypeId: suggestion.course.id,
    text: `${suggestion.course.cd} ${suggestion.course.title}`,
    slotType: 'COURSE',
    transactionType: suggestion.diff,
    transactionNk: suggestion.nk,
    screenReaderText: PlanFeedbackGenericSlotCourseScreenReaderTextStrategy[suggestion.diff](suggestion),
    cssClasses: PlanFeedbackGenericSlotCssClassStrategy[suggestion.diff],
    credits: suggestion.course.credits,
    slotNk: suggestion.course.nk,
    isLocked: suggestion.is_locked,
    flagInfo: suggestion.flagInfo
  }),
  course_group: (suggestion: FlaggedPlanFeedbackCourseGroup) => ({
    id: suggestion.id,
    slotTypeId: suggestion.course_group.id,
    text: `${suggestion.program_slot_obj.desc} placeholder`,
    slotType: 'COURSE_GROUP',
    transactionType: suggestion.diff,
    transactionNk: suggestion.nk,
    slotNk: suggestion.course_group.nk,
    screenReaderText: PlanFeedbackGenericSlotCourseGroupScreenReaderTextStrategy[suggestion.diff](suggestion),
    cssClasses: PlanFeedbackGenericSlotCssClassStrategy[suggestion.diff]
  }),
  program_slot: (suggestion: PlanFeedbackProgramSlot) => ({
    id: suggestion.id,
    slotTypeId: suggestion.program_slot_obj.id,
    text: PlanFeedbackServant.getTextForFeedbackProgramSlot(suggestion),
    slotType: 'PROGRAM_SLOT',
    slotNk: suggestion.program_slot_obj.nk,
    transactionType: suggestion.diff,
    transactionNk: suggestion.nk,
    screenReaderText: PlanFeedbackGenericSlotProgramSlotScreenReaderTextStrategy[suggestion.diff](suggestion),
    cssClasses: PlanFeedbackGenericSlotCssClassStrategy[suggestion.diff],
    comment: suggestion.comment
  })
});

export interface IPlanFeedbackGenericSlotBuilder {
  id: number;
  slotTypeId: number; // course id or course group id
  text: string;
  slotType: PlanFeedbackGenericSlotType;
  transactionType: planFeedbackTransactionType;
  cssClasses: string;
  transactionNk: string;
  screenReaderText: string;
  slotNk?: string;
  flagInfo?: FlaggedPlanFeedbackCourse['flagInfo'];
  comment?: string;
}

export class PlanFeedbackGenericSlotBuilder implements IPlanFeedbackGenericSlotBuilder {
  id: IPlanFeedbackGenericSlotBuilder['id'];
  text: IPlanFeedbackGenericSlotBuilder['text'];
  slotType: IPlanFeedbackGenericSlotBuilder['slotType'];
  transactionType: IPlanFeedbackGenericSlotBuilder['transactionType'];
  slotTypeId: IPlanFeedbackGenericSlotBuilder['slotTypeId'];
  cssClasses: IPlanFeedbackGenericSlotBuilder['cssClasses'];
  transactionNk: IPlanFeedbackGenericSlotBuilder['transactionNk'];
  screenReaderText: IPlanFeedbackGenericSlotBuilder['screenReaderText'];

  constructor(suggestion: Partial<FlaggedPlanFeedbackCourse>) {
    _4.merge(this, suggestion);
  }

  static build(suggestion: FlaggedPlanFeedbackCourse | PlanFeedbackProgramSlot | FlaggedPlanFeedbackCourseGroup) {
    return new PlanFeedbackGenericSlotBuilder(
      PlanFeedbackGenericSlotBuilderFns[PlanFeedbackGenericSlotBuilderStrategy.evaluate(suggestion)](suggestion)
    );
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/suggestions/components/plan_feedback/builders/plan_feedback_generic_slot_builder.ts